import { SystemStyleObject } from '@chakra-ui/react';
import {
  CounterBadgeSize,
  getCounterBadgeStyles,
} from '@allergan-data-labs/alle-elements-counter-badge';
import { ColorMode } from '@allergan-data-labs/alle-elements-core';
import { ButtonColorScheme, ButtonSizes, ButtonVariants } from './button';

export interface CounterBadgeStyleProps {
  colorMode?: ColorMode;
  count?: number;
  size?: CounterBadgeSize;
  buttonSize?: ButtonSizes;
  buttonVariant?: ButtonVariants;
  buttonColorScheme?: ButtonColorScheme;
  isDisabled?: boolean;
}

export const getButtonCounterBadgeStyles = ({
  colorMode = 'light',
  size = 'lg',
  count = 0,
  buttonSize = 'md',
  buttonVariant = 'solid',
  buttonColorScheme = 'action',
  isDisabled = false,
}: CounterBadgeStyleProps): SystemStyleObject => {
  const heightBadge: Record<ButtonSizes, string> = {
    lg: '24',
    md: '24',
    sm: '20',
    xs: '18',
  };

  const bgColor: Record<ButtonColorScheme, Record<ButtonVariants, string>> = {
    action: {
      solid: `${colorMode}.Section bg/Neutral/Base`,
      outline: `${colorMode}.Container/Neutral/Strong 2`,
      ghost: `${colorMode}.Container/Neutral/Strong 2`,
      tinted: `${colorMode}.Container/Neutral/Transparent interactive/Mute enable`,
    },
    brand: {
      solid: `${colorMode}.Section bg/Neutral/Base`,
      outline: `${colorMode}.Icon/Brand/Default`,
      ghost: `${colorMode}.Icon/Brand/Default`,
      tinted: `${colorMode}.Container/Neutral/Transparent interactive/Mute enable`,
    },
    muted: {
      solid: `${colorMode}.Container/Neutral/Strong 2`,
      outline: `${colorMode}.Container/Neutral/Strong 2`,
      ghost: `${colorMode}.Container/Neutral/Strong 2`,
      tinted: `${colorMode}.Container/Neutral/Transparent interactive/Mute enable`,
    },
    negative: {
      solid: `${colorMode}.Section bg/Neutral/Base`,
      outline: `${colorMode}.Container/Neutral/Strong 2`,
      ghost: `${colorMode}.Container/Neutral/Strong 2`,
      tinted: `${colorMode}.Container/Neutral/Transparent interactive/Mute enable`,
    },
    positive: {
      solid: `${colorMode}.Section bg/Neutral/Base`,
      outline: `${colorMode}.Container/Neutral/Strong 2`,
      ghost: `${colorMode}.Container/Neutral/Strong 2`,
      tinted: `${colorMode}.Container/Neutral/Transparent interactive/Mute enable`,
    },
  };

  const color: Record<ButtonColorScheme, Record<ButtonVariants, string>> = {
    action: {
      solid: `${colorMode}.Text/Neutral/Default`,
      outline: `${colorMode}.Text/Neutral/Inverted`,
      ghost: `${colorMode}.Text/Neutral/Inverted`,
      tinted: `light.Text/Neutral/Default`,
    },
    brand: {
      solid: `${colorMode}.Text/Neutral/Default`,
      outline: `${colorMode}.Text/Neutral/Inverted`,
      ghost: `${colorMode}.Text/Neutral/Inverted`,
      tinted: `light.Text/Neutral/Default`,
    },
    muted: {
      solid: `${colorMode}.Text/Neutral/Inverted`,
      outline: `${colorMode}.Text/Neutral/Inverted`,
      ghost: `${colorMode}.Text/Neutral/Inverted`,
      tinted: `light.Text/Neutral/Default`,
    },
    negative: {
      solid: `${colorMode}.Text/Neutral/Default`,
      outline: `${colorMode}.Text/Neutral/Inverted`,
      ghost: `${colorMode}.Text/Neutral/Inverted`,
      tinted: `light.Text/Neutral/Default`,
    },
    positive: {
      solid: `${colorMode}.Text/Neutral/Default`,
      outline: `${colorMode}.Text/Neutral/Inverted`,
      ghost: `${colorMode}.Text/Neutral/Inverted`,
      tinted: `light.Text/Neutral/Default`,
    },
  };

  const backgroundColor = isDisabled
    ? `${colorMode}.Icon/Neutral/Disabled/Default`
    : bgColor[buttonColorScheme][buttonVariant];

  const fontColor = isDisabled
    ? `${colorMode}.Text/Neutral/Default`
    : color[buttonColorScheme][buttonVariant];

  return {
    ...getCounterBadgeStyles({ colorMode, size, count }),
    width: '1.625rem',
    height: heightBadge[buttonSize],
    px: '2',
    py: '0',
    bg: backgroundColor,
    color: fontColor,
    borderRadius: 'Circle',
    backgroundColor,
  };
};

export interface ButtonCounteStyleProps {
  colorMode?: ColorMode;
  variant?: ButtonVariants;
  colorScheme?: ButtonColorScheme;
}

export const getButtonCounterStyles = ({
  colorMode = 'light',
  variant = 'solid',
  colorScheme = 'action',
}: ButtonCounteStyleProps): SystemStyleObject => {
  const bgColor: Record<ButtonColorScheme, Record<ButtonVariants, string>> = {
    action: {
      solid: `${colorMode}.Container/Neutral/Interactive/Pressed`,
      outline: `${colorMode}.Container/Neutral/Interactive/Pressed`,
      ghost: `${colorMode}.Container/Neutral/Interactive/Pressed`,
      tinted: `${colorMode}.Container/Neutral/Transparent interactive/Mute pressed`,
    },
    brand: {
      solid: `${colorMode}.Container/Brand/Interactive/Pressed`,
      outline: `${colorMode}.Container/Brand/Transparent interactive/Pressed`,
      ghost: `${colorMode}.Container/Brand/Transparent interactive/Pressed`,
      tinted: `${colorMode}.Container/Neutral/Transparent interactive/Mute pressed`,
    },
    muted: {
      solid: `${colorMode}.Container/Neutral/Interactive/Pressed`,
      outline: `${colorMode}.Container/Neutral/Interactive/Pressed`,
      ghost: `${colorMode}.Container/Neutral/Interactive/Pressed`,
      tinted: `${colorMode}.Container/Neutral/Transparent interactive/Mute pressed`,
    },
    negative: {
      solid: `${colorMode}.Container/Status/Interactive/Pressed negative`,
      outline: `${colorMode}.Container/Status/Transparent interactive/Pressed negative`,
      ghost: `${colorMode}.Container/Status/Transparent interactive/Pressed negative`,
      tinted: `${colorMode}.Container/Neutral/Tinted interactive/Enable`,
    },
    positive: {
      solid: `${colorMode}.Container/Status/Positive medium 3`,
      outline: `${colorMode}.Container/Status/Transparent interactive/Enable positive`,
      ghost: `${colorMode}.Container/Status/Transparent interactive/Enable positive`,
      tinted: `${colorMode}.Container/Neutral/Tinted interactive/Enable`,
    },
  };

  return {
    columnGap: '8',
    _active: {
      ':not(.static)': {
        backgroundColor: bgColor[colorScheme][variant],
      },
    },
  };
};
